import React, { useState } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Sparkles, Zap } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter. 411.mm3 — Two-pass meal analysis toggle.
 * A second AI pass on the same photo hunts for missed items (sauces, oil,
 * drinks) and wrong portions, then reconciles the macros. Costs ~2s extra
 * per scan so it's opt-in.
 */
const SettingsTwoPassMeal = () => {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();
  const [saving, setSaving] = useState(false);
  const enabled = !!user?.two_pass_meal_analysis;

  const L = (fr, en, es) => language === 'es' ? es : (language === 'en' ? en : fr);

  const handleToggle = async (checked) => {
    setSaving(true);
    try {
      const res = await api.put('/users/profile', { two_pass_meal_analysis: checked });
      updateUser(res.data);
      toast.success(
        checked
          ? L('Double passe activee', 'Two-pass enabled', 'Doble pasada activada')
          : L('Double passe desactivee', 'Two-pass disabled', 'Doble pasada desactivada')
      );
    } catch {
      toast.error(L('Erreur de sauvegarde', 'Save error', 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className="border-orange-500/30 transition-all"
      style={{
        background: 'linear-gradient(160deg, rgba(251,146,60,0.08) 0%, rgba(0,0,0,0.65) 100%)',
        backdropFilter: 'blur(14px)',
        boxShadow: '0 0 18px rgba(251,146,60,0.22), inset 0 1px 0 rgba(255,255,255,0.05)',
      }}
      data-testid="settings-two-pass-card"
    >
      <CardHeader>
        <CardTitle
          className="font-heading text-xl uppercase tracking-tight flex items-center gap-2 text-orange-100"
          style={{ textShadow: '0 0 8px rgba(251,146,60,0.5)' }}
        >
          <Sparkles className="w-5 h-5 text-orange-400" style={{ filter: 'drop-shadow(0 0 4px rgba(251,146,60,0.7))' }} />
          {L('Analyse repas — Double passe', 'Meal analysis — Two-pass', 'Analisis de comida — Doble pasada')}
        </CardTitle>
        <CardDescription className="text-orange-100/70">
          {L(
            'Une deuxieme passe IA scanne la meme photo pour reperer les aliments oublies (sauces, huile, boissons, garnitures) et corriger les portions mal evaluees.',
            'A second AI pass scans the same photo to catch missed foods (sauces, oil, drinks, garnishes) and correct mis-estimated portions.',
            'Una segunda pasada de IA escanea la misma foto para detectar alimentos olvidados (salsas, aceite, bebidas) y corregir porciones mal estimadas.'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4 p-4 rounded-lg bg-black/40 border border-orange-500/20">
          <div className="flex items-center gap-3">
            <Zap className={`w-5 h-5 ${enabled ? 'text-amber-300' : 'text-white/40'}`}
              style={enabled ? { filter: 'drop-shadow(0 0 6px rgba(252,211,77,0.7))' } : {}} />
            <div>
              <Label htmlFor="two-pass-switch" className="text-orange-100 font-semibold cursor-pointer">
                {enabled
                  ? L('Active — precision maximale', 'Active — max precision', 'Activo — maxima precision')
                  : L('Passe unique — plus rapide', 'Single pass — faster', 'Pasada unica — mas rapido')}
              </Label>
              <p className="text-xs text-orange-200/60 mt-1">
                {enabled
                  ? L('+2s par analyse, macros plus fiables', '+2s per scan, more reliable macros', '+2s por escaneo, macros mas confiables')
                  : L('Analyse standard rapide (~3s)', 'Standard fast analysis (~3s)', 'Analisis estandar rapido (~3s)')}
              </p>
            </div>
          </div>
          <Switch
            id="two-pass-switch"
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={saving}
            data-testid="two-pass-toggle"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default SettingsTwoPassMeal;
